import type { ReactNode } from 'react'

/**
 * Page chrome: the outer padding every page shares, and the heading block from
 * Tailwind UI's page-headings set
 * (resources/tailwind-examples/application-ui/headings/page-headings/with_actions.jsx),
 * with colours moved onto shell-* tokens.
 *
 * Having this in one place is what stops each page inventing its own title size
 * and gutter — the drift that makes an app look assembled rather than designed.
 */

type PageProps = {
  children: ReactNode
  /**
   * `wide` when the page needs room for two columns — it hosts a
   * `DetailPaneLayout` (a list that opens a detail pane beside itself), an
   * admin table, or a deliberate multi-column card layout. `default` (a centred
   * `max-w-5xl` column) everywhere else, including plain list pages with no
   * pane. Both widths are capped; neither goes edge-to-edge.
   */
  width?: 'default' | 'wide'
}

export const Page = ({ children, width = 'default' }: PageProps) => (
  <div className={`mx-auto px-4 py-6 sm:px-6 lg:px-8 ${width === 'wide' ? 'max-w-[96rem]' : 'max-w-5xl'}`}>
    {children}
  </div>
)

type PageHeaderProps = {
  title: string
  /** One sentence on what the page is for; omitted where the title says it all. */
  description?: string
  /** Buttons, aligned right on wide screens and stacked underneath on narrow ones. */
  actions?: ReactNode
}

export const PageHeader = ({ title, description, actions }: PageHeaderProps) => (
  <div className="mb-6 md:flex md:items-start md:justify-between md:gap-x-6">
    <div className="min-w-0 flex-1">
      <h1 className="text-2xl/7 font-bold text-shell-ink sm:truncate sm:text-3xl sm:tracking-tight">{title}</h1>
      {description && <p className="mt-2 text-sm text-shell-ink-secondary">{description}</p>}
    </div>
    {actions && <div className="mt-4 flex shrink-0 flex-wrap items-center gap-3 md:mt-0">{actions}</div>}
  </div>
)

type SectionProps = {
  children: ReactNode
  className?: string
}

/** A card, per layout/cards — the container every page section sits in. */
export const Card = ({ children, className = '' }: SectionProps) => (
  <div
    className={`overflow-hidden rounded-lg bg-shell-surface shadow-sm outline-1 -outline-offset-1 outline-shell-border ${className}`}
  >
    {children}
  </div>
)

type CardHeaderProps = {
  title: string
  description?: string
  actions?: ReactNode
}

/** Card heading with an optional action, per headings/card-headings. */
export const CardHeader = ({ title, description, actions }: CardHeaderProps) => (
  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-shell-border px-4 py-4 sm:px-6">
    <div className="min-w-0">
      <h2 className="text-base font-semibold text-shell-ink">{title}</h2>
      {description && <p className="mt-1 text-sm text-shell-ink-secondary">{description}</p>}
    </div>
    {actions && <div className="flex shrink-0 items-center gap-x-2">{actions}</div>}
  </div>
)

export const CardBody = ({ children, className = '' }: SectionProps) => (
  <div className={`px-4 py-4 sm:px-6 ${className}`}>{children}</div>
)
